// ============================================================================
// CLEO'S SECRET — GraceCancelTrigger
// File: src/components/GraceCancelTrigger.jsx
//
// Two presentations of the same affordance:
//   variant="link"  — italic underline-on-hover text, used inline below the
//                     receipt note on EnrolmentSuccess.
//   variant="card"  — papyrus-on-sand card with canonical <Eyebrow>
//                     "Recent enrolment" label, used in ClientPortal when
//                     the patient has a within-grace plan.
//
// Both surface the same shared <ConfirmationModal>. After a successful
// cancel call, the modal stays open with a confirmation message for ~2.4s
// then dismisses; the trigger element disappears so the patient cannot
// click it again.
//
// Brand-tone (per memory feedback_calm_cta_over_urgency.md):
//   Sand + subtle borders, never urgent gold pressure styling. The trigger
//   reads as a quiet option, not a CTA being pushed at the patient.
//
// Props:
//   variant      'link' | 'card' — required
//   plan         { id, totalPaidCents, paymentOption, packageDisplayName, hoursRemaining }
//   email        string  — patient email; required for the API call
//   onCancelled  ()=>void — optional; fires after success-message settles
// ============================================================================

import React, { useState } from 'react';
import { FF, P, T, Eyebrow } from '../lib/design-tokens';
import ConfirmationModal from './ConfirmationModal';

const SUCCESS_DISMISS_MS = 2400;

function formatAUD(cents) {
  if (cents == null) return '';
  return '$' + new Intl.NumberFormat('en-AU').format(Math.floor(cents / 100));
}

export default function GraceCancelTrigger(props) {
  const { variant, plan, email, onCancelled } = props;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [hideTrigger, setHideTrigger] = useState(false);

  if (hideTrigger) return null;
  if (!plan || !plan.id) return null;

  function reset() {
    setBusy(false);
    setError(null);
    setSuccessMsg(null);
  }

  function handleOpen() {
    reset();
    setOpen(true);
  }
  function handleClose() {
    if (busy) return;
    reset();
    setOpen(false);
  }

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/program-plan-cancel-grace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, planId: plan.id }),
      });
      const body = await res.json().catch(function() { return {}; });
      if (!res.ok) {
        setBusy(false);
        setError(body.message || body.error || 'Could not initiate the refund. Please try again.');
        return;
      }
      // Success: swap modal to confirmation, then dismiss + hide trigger.
      setBusy(false);
      setSuccessMsg(
        <div style={{ textAlign: 'center' }}>
          <p style={{ ...T.premise, fontSize: 'clamp(18px, 1.9vw, 24px)', color: P.BR2, margin: '0 0 12px' }}>
            Cancellation in progress.
          </p>
          <p style={{ ...T.bodyMd, color: P.TL, margin: 0, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
            Your refund typically lands in 5-10 business days. A confirmation will be sent to your email.
          </p>
        </div>
      );
      setTimeout(function() {
        setOpen(false);
        setHideTrigger(true);
        if (onCancelled) onCancelled();
      }, SUCCESS_DISMISS_MS);
    } catch (err) {
      setBusy(false);
      setError(err.message || 'Network error. Please try again.');
    }
  }

  // Modal content
  const modal = (
    <ConfirmationModal
      open={open}
      onClose={handleClose}
      premise="Cancelling within the 48-hour window."
      title={
        <React.Fragment>
          Refund your enrolment in <em>full?</em>
        </React.Fragment>
      }
      body={
        <React.Fragment>
          Your card will be refunded for <strong style={{ color: P.INK, fontWeight: 600 }}>{formatAUD(plan.totalPaidCents)}</strong>.
          {plan.paymentOption === 'three_instalments' ? ' Any pending instalments will be cancelled.' : ''} This cannot be undone.
        </React.Fragment>
      }
      confirmLabel="Yes, cancel my enrolment"
      cancelLabel="Keep my enrolment"
      busyLabel="Cancelling..."
      onConfirm={handleConfirm}
      busy={busy}
      error={error}
      successMessage={successMsg}
    />
  );

  if (variant === 'card') {
    return (
      <React.Fragment>
        <section style={{
          background: P.PAP,
          border: '1px solid ' + P.SAND3,
          padding: 'clamp(20px, 2.4vw, 28px)',
          marginBottom: 'clamp(24px, 3vw, 36px)',
        }}>
          <div style={{ marginBottom: 14 }}>
            <Eyebrow>Recent enrolment</Eyebrow>
          </div>
          <div style={{ ...T.bodyMd, color: P.TX, marginBottom: 14 }}>
            {plan.packageDisplayName ? plan.packageDisplayName + ' · ' : ''}
            <span style={{ color: P.TL }}>
              Refundable in full for the next <strong style={{ color: P.INK, fontWeight: 600 }}>{plan.hoursRemaining}h</strong>.
            </span>
          </div>
          <CancelLink onClick={handleOpen} />
        </section>
        {modal}
      </React.Fragment>
    );
  }

  // variant="link" — for inline use below other content (EnrolmentSuccess)
  return (
    <React.Fragment>
      <div style={{ marginTop: 18, textAlign: 'center' }}>
        <CancelLink onClick={handleOpen} />
      </div>
      {modal}
    </React.Fragment>
  );
}

// Underlined italic text-link affordance — calm, not a button-styled CTA.
function CancelLink({ onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        ...T.bodySmItalic,
        color: P.TL,
        textDecoration: hover ? 'underline' : 'none',
        textUnderlineOffset: 4,
        textDecorationColor: 'rgba(154,116,48,0.55)',
        fontFamily: FF,
      }}
    >
      Cancel within 48 hours →
    </button>
  );
}
